import React from "react";
import { Link } from "react-router-dom";
import Logo from "../../assets/img/LogoÑuñoa.png";
import { FiMapPin, FiMail, FiPhone, FiInstagram, FiFacebook, FiYoutube, FiExternalLink } from "react-icons/fi";
import { FaXTwitter } from "react-icons/fa6";

const SOCIAL = [
    { icon: <FiFacebook size={18} />, href: "https://www.facebook.com/corporaciondedeportes/?locale=es_LA", label: "Facebook" },
    { icon: <FiInstagram size={18} />, href: "https://www.instagram.com/nunoadeportes/", label: "Instagram" },
    { icon: <FaXTwitter size={16} />, href: "https://x.com/nunoadeportes", label: "X" },
    { icon: <FiYoutube size={18} />, href: "https://youtube.com/@Deportes-Ñuñoa", label: "YouTube" },
];

// «Información General» y «Términos y Condiciones» apuntaban a "#": se sacan hasta
// que exista la página de destino.
const LINKS = [
    { label: "Nosotros", href: "https://nunoadeportes.cl/" },
    { label: "Política de Privacidad", to: "/privacy" },
    { label: "Ñuñoa.cl", href: "https://www.nunoa.cl/" },
];

// Título de columna con una rayita amarilla debajo.
const TITULO = "relative font-bold text-white text-sm uppercase tracking-widest mb-5 pb-3 after:content-[''] after:absolute after:left-0 after:bottom-0 after:w-8 after:h-1 after:rounded-full after:bg-secondary";
// `hover:!text-*` porque el a:hover global de antd le gana a una clase simple.
const LINK = "text-sm text-blue-100 hover:!text-secondary transition-colors";

export default function Footer() {
    return (
        <footer id="contacto" className="relative overflow-hidden bg-[#00266b] text-white mt-0 scroll-mt-16">
            {/* Franja amarilla superior */}
            <div aria-hidden className="h-1.5 bg-secondary" />
            {/* Patrón de puntos muy suave */}
            <div aria-hidden className="absolute inset-0 opacity-[.08] pointer-events-none" style={{
                backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)', backgroundSize: '24px 24px'
            }} />

            {/* Main footer */}
            <div className="relative max-w-screen-xl mx-auto px-6 py-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">

                {/* Brand */}
                <div className="lg:col-span-1">
                    <div className="flex items-center gap-3 mb-4">
                        <img src={Logo} alt="" className="h-12 w-auto" />
                        <span className="flex flex-col leading-tight">
                            <span className="font-extrabold text-white">Ñuñoa Deportes</span>
                            <span className="text-xs text-blue-200">Talleres municipales</span>
                        </span>
                    </div>
                    <p className="text-blue-100 text-sm leading-relaxed mb-5">
                        Fomentamos el deporte y la actividad física en la comunidad de Ñuñoa.
                    </p>
                    <div className="flex gap-2.5">
                        {SOCIAL.map(s => (
                            <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer"
                                className="w-10 h-10 rounded-full bg-white/10 text-white hover:bg-secondary hover:!text-[#00266b] flex items-center justify-center transition-colors"
                                aria-label={s.label}>
                                {s.icon}
                            </a>
                        ))}
                    </div>
                </div>

                {/* Contacto */}
                <div>
                    <h4 className={TITULO}>Contacto</h4>
                    <ul className="space-y-3.5">
                        <li className="flex items-start gap-3 text-sm text-blue-100">
                            <FiMapPin size={16} className="mt-0.5 text-secondary flex-shrink-0" />
                            <span>Juan Moya 1370, Ñuñoa<br />Región Metropolitana</span>
                        </li>
                        <li>
                            <a href="mailto:contacto@nunoadeportes.cl" className={`flex items-center gap-3 ${LINK}`}>
                                <FiMail size={16} className="text-secondary flex-shrink-0" />
                                contacto@nunoadeportes.cl
                            </a>
                        </li>
                        <li>
                            <a href="tel:+56944002092" className={`flex items-center gap-3 ${LINK}`}>
                                <FiPhone size={16} className="text-secondary flex-shrink-0" />
                                +56 9 4400 2092
                            </a>
                        </li>
                    </ul>
                </div>

                {/* Links */}
                <div>
                    <h4 className={TITULO}>Links</h4>
                    <ul className="space-y-3">
                        {LINKS.map(l => (
                            <li key={l.label}>
                                {l.to
                                    ? <Link to={l.to} className={LINK}>{l.label}</Link>
                                    : <a href={l.href} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-1.5 ${LINK}`}>
                                        {l.label} <FiExternalLink size={12} aria-hidden className="opacity-60" />
                                        <span className="sr-only">(abre en otra pestaña)</span>
                                    </a>}
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Horario */}
                <div>
                    <h4 className={TITULO}>Atención</h4>
                    <ul className="space-y-2.5">
                        {[
                            { days: 'Lunes – Viernes', hours: '09:00 – 18:00' },
                            { days: 'Sábado', hours: '09:00 – 14:00' },
                            { days: 'Domingo', hours: 'Cerrado' },
                        ].map(h => (
                            <li key={h.days} className="flex justify-between gap-4 text-sm">
                                <span className="text-blue-100">{h.days}</span>
                                <span className={h.hours === 'Cerrado' ? 'text-blue-200' : 'text-secondary font-bold tabular-nums'}>{h.hours}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            {/* Bottom bar */}
            <div className="relative border-t border-white/10">
                <div className="max-w-screen-xl mx-auto px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-2">
                    <p className="text-xs text-blue-200 m-0">© {new Date().getFullYear()} Corporación Municipal de Deportes de Ñuñoa</p>
                    <p className="text-xs text-blue-200 m-0">Todos los derechos reservados</p>
                </div>
            </div>
        </footer>
    );
}
